import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { useLanguagePreference } from '@/hooks/useLanguagePreference';
import { cn } from '@/lib/utils';

import { AdminHeader } from './AdminHeader';
import { AdminSidebar } from './AdminSidebar';

const LAYOUT = {
  sidebarExpandedWidth: 216,
  sidebarCollapsedWidth: 64,
  mobileBreakpoint: 1024,
} as const;

interface AdminLayoutProps {
  className?: string;
}

export const AdminLayout = memo(function AdminLayout({
  className,
}: AdminLayoutProps) {
  useLanguagePreference();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < LAYOUT.mobileBreakpoint;
      setIsMobile(mobile);
      if (mobile) {
        setIsMobileSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleSidebarToggle = useCallback(() => {
    if (isMobile) {
      setIsMobileSidebarOpen(prev => !prev);
    } else {
      setIsSidebarExpanded(prev => !prev);
    }
  }, [isMobile]);

  const closeMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false);
  }, []);

  const currentSidebarWidth = isMobile
    ? 0
    : isSidebarExpanded
      ? LAYOUT.sidebarExpandedWidth
      : LAYOUT.sidebarCollapsedWidth;

  const mainStyle = useMemo(
    () => ({
      paddingLeft: `${currentSidebarWidth}px`,
      height: '100vh',
      display: 'flex',
      flexDirection: 'column' as const,
    }),
    [currentSidebarWidth],
  );

  const sidebarStyle = useMemo(
    () => ({
      width: isMobile
        ? `${LAYOUT.sidebarExpandedWidth}px`
        : `${isSidebarExpanded ? LAYOUT.sidebarExpandedWidth : LAYOUT.sidebarCollapsedWidth}px`,
    }),
    [isMobile, isSidebarExpanded],
  );

  return (
    <div
      className={cn(
        'bg-muted flex h-screen flex-col overflow-hidden',
        className,
      )}
    >
      {isMobile && isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 transition-opacity lg:hidden"
          onClick={closeMobileSidebar}
        />
      )}

      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen transition-all duration-300 ease-in-out',
          isMobile && !isMobileSidebarOpen && '-translate-x-full',
          isMobile && isMobileSidebarOpen && 'translate-x-0',
        )}
        style={sidebarStyle}
      >
        <AdminSidebar
          isExpanded={isMobile ? true : isSidebarExpanded}
          onToggle={handleSidebarToggle}
        />
      </aside>

      <div
        className="flex h-full flex-col overflow-hidden transition-all duration-300 ease-in-out"
        style={mainStyle}
      >
        <AdminHeader
          className="shrink-0"
          onSidebarToggle={handleSidebarToggle}
          isMobile={isMobile}
        />

        <main className="flex flex-1 flex-col overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
});

export default AdminLayout;
